import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS,
  SyncWorkflowRemoveFileResponse,
  SyncWorkflowUploadFileResponse,
  WorkflowFormSubmissionExternal,
  WorkflowFormSubmissionFileExternal,
} from '@workflows/types';
import { environment } from '@environment/environment';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { WorkflowsTranslations } from '@core/types';
import { PhotoQueueService } from './photo-queue.service';
import { WorkflowStateService } from './workflow-state.service';
import { classifyWorkflowError } from './workflow-sync-result';
import type {
  PhotoQueueEntry,
  PhotoQueueTarget,
  PhotoQueueTask,
} from './input-photos-queue.types';
import type {
  WorkflowFileFailureContext,
  WorkflowFileFieldChange,
  WorkflowFileFieldsSyncResult,
  WorkflowFileFieldTarget,
  WorkflowFileSyncFailure,
} from './workflow-file-field.types';
import {
  getWorkflowFileOperationId,
  getWorkflowFileOrigin,
  getWorkflowMimetypeFromFilename,
  resetWorkflowFileOperationId,
  setWorkflowFileOrigin,
} from './workflow-file-field.utils';

const QUEUED_RETRY_DELAY_MS = 30_000;

type FileOperationOutcome = 'done' | 'queued' | WorkflowFileSyncFailure;

/**
 * Синхронизация полей inputFiles: загрузка новых файлов и удаление убранных.
 *
 * Без сети (или при временной ошибке сервера) операции записываются в очередь файлов
 * на устройстве (общая с фото: задачи по полю, повторы, владелец) и отправляются позже.
 * Ошибки, которые повтор не исправит (тип, размер, количество), возвращаются полю.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowFileFieldsSyncService {
  private readonly stateService = inject(WorkflowStateService);
  private readonly photoQueueService = inject(PhotoQueueService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  /**
   * Файлы поля: серверные (кроме ожидающих удаления) и загрузки из очереди на устройстве
   */
  async loadFieldFiles(
    target: WorkflowFileFieldTarget,
    fieldKey: string,
    submission: WorkflowFormSubmissionExternal | null,
  ): Promise<File[]> {
    const fieldEntries = (await this.getQueueEntriesSafe()).filter(entry =>
      this.isFieldTask(entry.task, target, fieldKey),
    );
    const removedIds = new Set<number>();

    for (const { task } of fieldEntries) {
      if (task.operation === 'remove') {
        removedIds.add(task.createdSubmissionFileId);
      }
    }

    const serverFiles = (submission?.files ?? []).filter(
      file => file.fieldKey === fieldKey && !removedIds.has(file.id),
    );
    const serverFileIds = new Set(serverFiles.map(file => file.id));
    const loadedServerFiles = await Promise.all(
      serverFiles.map(file => this.createServerFile(file)),
    );
    const queuedFiles = fieldEntries
      .filter(
        ({ task }) =>
          task.operation === 'upload' &&
          !(
            task.state === 'succeeded' &&
            task.result &&
            serverFileIds.has(task.result.createdSubmissionFileId)
          ),
      )
      .map(entry => this.createQueuedFile(entry));

    return [...loadedServerFiles, ...queuedFiles].filter(
      (file): file is File => file !== null,
    );
  }

  /**
   * Применить изменения файлов полей формы. Возвращает файлы, которые не удалось сохранить.
   */
  async syncFieldChanges(
    target: WorkflowFileFieldTarget,
    changes: readonly WorkflowFileFieldChange[],
  ): Promise<WorkflowFileFieldsSyncResult> {
    const failures: WorkflowFileSyncFailure[] = [];
    let queued = 0;

    for (const change of changes) {
      const uploads = change.upload.map(
        file => () => this.uploadFile(target, change.fieldKey, file),
      );
      const removals = change.remove.map(
        file => () => this.removeFile(target, change.fieldKey, file),
      );
      // Backend проверяет maxFilesCount до загрузки: при замене файла на пределе
      // сначала удаляем, иначе загрузка будет отклонена
      const removeFirst =
        change.maxFilesCount !== undefined &&
        change.existingCount + change.upload.length > change.maxFilesCount;
      const operations = removeFirst
        ? [...removals, ...uploads]
        : [...uploads, ...removals];

      for (const operation of operations) {
        const outcome = await operation();

        if (outcome === 'queued') {
          queued++;
        } else if (outcome !== 'done') {
          failures.push(outcome);
        }
      }
    }

    return { failures, queued };
  }

  private async uploadFile(
    target: WorkflowFileFieldTarget,
    fieldKey: string,
    file: File,
  ): Promise<FileOperationOutcome> {
    const operationId = getWorkflowFileOperationId(file, 'upload');

    if (this.isOffline()) {
      return this.queueUpload(target, fieldKey, file, operationId);
    }

    const formData = new FormData();

    formData.append('file', file);

    let response: SyncWorkflowUploadFileResponse;

    try {
      response = await firstValueFrom(
        this.stateService.syncWorkflowUploadFile(
          target.requestNumber,
          target.workflowId,
          target.nodeId,
          target.formId,
          fieldKey,
          formData,
          operationId,
        ),
      );
    } catch (error) {
      const classification = classifyWorkflowError(error);

      if (classification.kind === 'retryable') {
        return this.queueUpload(target, fieldKey, file, operationId);
      }

      return this.createFailure(
        fieldKey,
        file,
        'upload',
        classification.error?.failureReason,
        classification.error?.message,
      );
    }

    if (!response.isOk) {
      const context = 'context' in response ? response.context : undefined;

      return this.createFailure(
        fieldKey,
        file,
        'upload',
        response.failureReason,
        undefined,
        // Лимиты есть только в ответе о нарушении ограничений файла
        context && 'fieldKey' in context ? context : undefined,
      );
    }

    setWorkflowFileOrigin(file, {
      kind: 'server',
      submissionFileId: response.data.createdSubmissionFileId,
    });

    if (response.data.uploadedFile) {
      this.stateService.addSubmissionFile(
        target.workflowId,
        target.nodeId,
        target.formId,
        {
          id: response.data.createdSubmissionFileId,
          formSubmissionId: response.data.uploadedToSubmissionId,
          fieldKey,
          logisticRequestFileId: response.data.uploadedFile.id,
          logisticRequestFile: response.data.uploadedFile,
          createdAt: new Date().toISOString(),
          createdId: 0,
        },
      );
    }

    return 'done';
  }

  private async removeFile(
    target: WorkflowFileFieldTarget,
    fieldKey: string,
    file: File,
  ): Promise<FileOperationOutcome> {
    const origin = getWorkflowFileOrigin(file);

    if (!origin) {
      return 'done';
    }

    let submissionFileId: number;

    if (origin.kind === 'queued') {
      try {
        const task = (await this.photoQueueService.getAll()).find(
          queuedTask => queuedTask.id === origin.taskId,
        );

        // Загрузка из очереди ещё не прошла: достаточно отменить задачу
        if (
          task?.operation !== 'upload' ||
          task.state !== 'succeeded' ||
          !task.result
        ) {
          await this.photoQueueService.cancel(origin.taskId);

          return 'done';
        }

        // Загрузка уже прошла: файл есть на сервере, удаляем его там
        submissionFileId = task.result.createdSubmissionFileId;
        await this.photoQueueService.acknowledgeSuccess(task.id);
      } catch (error) {
        return this.createStorageFailure(fieldKey, file, 'remove', error);
      }
    } else {
      submissionFileId = origin.submissionFileId;
    }

    const operationId = getWorkflowFileOperationId(file, 'remove');

    if (this.isOffline()) {
      return this.queueRemove(
        target,
        fieldKey,
        file,
        submissionFileId,
        operationId,
      );
    }

    let response: SyncWorkflowRemoveFileResponse;

    try {
      response = await firstValueFrom(
        this.stateService.syncWorkflowRemoveFile(
          target.requestNumber,
          target.workflowId,
          target.nodeId,
          target.formId,
          fieldKey,
          submissionFileId,
          operationId,
        ),
      );
    } catch (error) {
      const classification = classifyWorkflowError(error);

      if (classification.kind === 'retryable') {
        return this.queueRemove(
          target,
          fieldKey,
          file,
          submissionFileId,
          operationId,
        );
      }

      return this.createFailure(
        fieldKey,
        file,
        'remove',
        classification.error?.failureReason,
        classification.error?.message,
      );
    }

    if (
      !response.isOk &&
      response.failureReason !==
        SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.submissionFileNotFound
    ) {
      return this.createFailure(
        fieldKey,
        file,
        'remove',
        response.failureReason,
      );
    }

    this.stateService.removeSubmissionFile(
      target.workflowId,
      target.formId,
      submissionFileId,
    );

    return 'done';
  }

  private async queueUpload(
    target: WorkflowFileFieldTarget,
    fieldKey: string,
    file: File,
    operationId: string,
  ): Promise<FileOperationOutcome> {
    const now = Date.now();

    try {
      await this.photoQueueService.addUploadTask(
        {
          schemaVersion: 2,
          id: operationId,
          photoId: operationId,
          operation: 'upload',
          target: this.toQueueTarget(target, fieldKey),
          createdAt: now,
          attemptCount: 0,
          state: 'retry_wait',
          nextAttemptAt: now + QUEUED_RETRY_DELAY_MS,
          blobKey: operationId,
        },
        {
          blobKey: operationId,
          fileName: file.name,
          mimeType: file.type,
          lastModified: file.lastModified,
          blob: file,
        },
      );
    } catch (error) {
      return this.createStorageFailure(fieldKey, file, 'upload', error);
    }

    setWorkflowFileOrigin(file, { kind: 'queued', taskId: operationId });

    return 'queued';
  }

  private async queueRemove(
    target: WorkflowFileFieldTarget,
    fieldKey: string,
    file: File,
    submissionFileId: number,
    operationId: string,
  ): Promise<FileOperationOutcome> {
    const now = Date.now();

    try {
      await this.photoQueueService.addRemoveTask({
        schemaVersion: 2,
        id: operationId,
        photoId: operationId,
        operation: 'remove',
        target: this.toQueueTarget(target, fieldKey),
        createdAt: now,
        attemptCount: 0,
        state: 'retry_wait',
        nextAttemptAt: now + QUEUED_RETRY_DELAY_MS,
        createdSubmissionFileId: submissionFileId,
      });
    } catch (error) {
      return this.createStorageFailure(fieldKey, file, 'remove', error);
    }

    return 'queued';
  }

  private async getQueueEntriesSafe(): Promise<PhotoQueueEntry[]> {
    try {
      return await this.photoQueueService.getEntries();
    } catch (error) {
      // Очередь недоступна: показываем серверное состояние поля
      console.error('Failed to read queued files of the field:', error);

      return [];
    }
  }

  private async createServerFile(
    submissionFile: WorkflowFormSubmissionFileExternal,
  ): Promise<File | null> {
    const fileData = submissionFile.logisticRequestFile;

    if (!fileData) {
      return null;
    }

    const file = new File([], fileData.originFilename || fileData.filename, {
      type: getWorkflowMimetypeFromFilename(fileData.filename),
    });
    // Размер берём HEAD-запросом, не скачивая файл
    const size = await this.fetchFileSize(
      environment.downloadFilesUrl + fileData.path,
    );

    Object.defineProperty(file, 'size', {
      value: size,
      writable: false,
      configurable: true,
    });
    setWorkflowFileOrigin(file, {
      kind: 'server',
      submissionFileId: submissionFile.id,
    });

    return file;
  }

  private createQueuedFile({ task, blobRecord }: PhotoQueueEntry): File | null {
    if (!blobRecord) {
      return null;
    }

    const file = new File([blobRecord.blob], blobRecord.fileName, {
      type: blobRecord.mimeType,
      lastModified: blobRecord.lastModified,
    });

    setWorkflowFileOrigin(file, { kind: 'queued', taskId: task.id });

    return file;
  }

  private async fetchFileSize(fileUrl: string): Promise<number> {
    try {
      const response = await fetch(fileUrl, { method: 'HEAD' });
      const contentLength = response.ok
        ? response.headers.get('Content-Length')
        : null;

      return contentLength ? parseInt(contentLength, 10) : 0;
    } catch (error) {
      console.warn(`Could not fetch size for file ${fileUrl}:`, error);

      return 0;
    }
  }

  private isFieldTask(
    task: PhotoQueueTask,
    target: WorkflowFileFieldTarget,
    fieldKey: string,
  ): boolean {
    return (
      task.target.requestNumber === target.requestNumber &&
      task.target.workflowId === target.workflowId &&
      task.target.nodeId === target.nodeId &&
      task.target.formId === target.formId &&
      task.target.fieldId === fieldKey
    );
  }

  private toQueueTarget(
    target: WorkflowFileFieldTarget,
    fieldKey: string,
  ): PhotoQueueTarget {
    return { ...target, fieldId: fieldKey };
  }

  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && !navigator.onLine;
  }

  private createStorageFailure(
    fieldKey: string,
    file: File,
    operation: WorkflowFileSyncFailure['operation'],
    error: unknown,
  ): WorkflowFileSyncFailure {
    console.error('Failed to queue file operation on the device:', error);

    return {
      fieldKey,
      file,
      operation,
      message: this.outkeepTranslateService.instant(
        WorkflowsTranslations.FileNotSavedLocally,
      ),
    };
  }

  private createFailure(
    fieldKey: string,
    file: File,
    operation: WorkflowFileSyncFailure['operation'],
    failureReason?: string,
    fallbackMessage?: string,
    context?: WorkflowFileFailureContext,
  ): WorkflowFileSyncFailure {
    // Окончательная ошибка: следующая попытка пойдёт с новым operationId
    resetWorkflowFileOperationId(file, operation);

    return {
      fieldKey,
      file,
      operation,
      message: this.getFailureMessage(
        operation,
        failureReason,
        fallbackMessage,
        context,
      ),
    };
  }

  private getFailureMessage(
    operation: WorkflowFileSyncFailure['operation'],
    failureReason?: string,
    fallbackMessage?: string,
    context?: WorkflowFileFailureContext,
  ): string {
    switch (failureReason) {
      case SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.invalidFileMimetype:
        return this.outkeepTranslateService.instant(
          WorkflowsTranslations.FileInvalidType,
        );
      case SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.fileTooLarge:
        return this.outkeepTranslateService.instant(
          WorkflowsTranslations.FileTooLarge,
          { max: Math.round((context?.maxFileSizeBytes ?? 0) / 1_000_000) },
        );
      case SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.tooManyFiles:
        return this.outkeepTranslateService.instant(
          WorkflowsTranslations.FileTooMany,
          { max: context?.maxFilesCount ?? 0 },
        );
    }

    const generic = this.outkeepTranslateService.instant(
      operation === 'upload'
        ? WorkflowsTranslations.FileUploadFailed
        : WorkflowsTranslations.FileRemoveFailed,
    );
    const details = failureReason ?? fallbackMessage;

    return details ? `${generic}: ${details}` : generic;
  }
}
